/**
 * Session Management for CorePanelReact
 * Handles session timeout, warnings, and secure session management
 */

export interface SessionConfig {
  timeoutMinutes: number
  warningMinutes: number
  checkIntervalMs: number
  secure: boolean
  enabled: boolean // Whether session timeout is enabled
}

export interface SessionState {
  isActive: boolean
  lastActivity: number
  warningShown: boolean
  timeRemaining: number
  initializedAt?: number // Track when session was initialized
}

class SessionManager {
  private config: SessionConfig
  private state: SessionState
  private intervalId: NodeJS.Timeout | null = null
  private warningCallback?: (timeRemaining: number) => void
  private timeoutCallback?: () => void
  private activityCallbacks: (() => void)[] = []

  constructor(config: Partial<SessionConfig> = {}) {
    this.config = {
      timeoutMinutes: 30, // 30 minutes instead of 120
      warningMinutes: 5,  // Show warning 5 minutes before timeout
      checkIntervalMs: 1000, // Check every second
      secure: true,
      enabled: true, // Session timeout enabled by default
      ...config
    }

    this.state = {
      isActive: false,
      lastActivity: Date.now(),
      warningShown: false,
      timeRemaining: this.config.timeoutMinutes * 60 * 1000,
      initializedAt: undefined
    }

    this.setupEventListeners()
  }

  /**
   * Update configuration (e.g., from database settings)
   */
  public updateConfig(config: Partial<SessionConfig>): void {
    const oldTimeout = this.config.timeoutMinutes
    this.config = { ...this.config, ...config }
    
    console.log('[SessionManager] Config updated:', {
      oldTimeout,
      newTimeout: this.config.timeoutMinutes,
      enabled: this.config.enabled
    })
    
    // If disabled, stop monitoring
    if (!this.config.enabled) {
      this.stopMonitoring()
      this.state.isActive = false
    }
    // Note: timeRemaining will be updated when initialize() is called, which calls resetSession()
  }

  /**
   * Initialize session manager
   */
  public initialize(): void {
    console.log('[SessionManager] Initializing with config:', {
      enabled: this.config.enabled,
      timeoutMinutes: this.config.timeoutMinutes
    })
    
    // Only initialize if session timeout is enabled
    if (!this.config.enabled) {
      this.state.isActive = false
      this.stopMonitoring()
      return
    }
    
    // Stop any existing monitoring before resetting
    this.stopMonitoring()
    
    // Reset session with current config (this will use the updated timeoutMinutes)
    this.resetSession()
    
    // Start monitoring
    this.startMonitoring()
  }

  /**
   * Set callbacks for session events
   */
  public setCallbacks(callbacks: {
    onWarning?: (timeRemaining: number) => void
    onTimeout?: () => void
  }): void {
    this.warningCallback = callbacks.onWarning
    this.timeoutCallback = callbacks.onTimeout
  }

  /**
   * Add activity callback (called when user is active)
   */
  public addActivityCallback(callback: () => void): void {
    this.activityCallbacks.push(callback)
  }

  /**
   * Record user activity
   */
  public recordActivity(): void {
    const now = Date.now()
    this.state.lastActivity = now
    this.state.warningShown = false
    this.state.timeRemaining = this.config.timeoutMinutes * 60 * 1000
    // Update initializedAt to prevent immediate warning after extending
    this.state.initializedAt = now
    
    // Removed console.log for cleaner testing
    // console.log('[SessionManager] Activity recorded, session extended:', {
    //   timeoutMinutes: this.config.timeoutMinutes,
    //   timeRemaining: `${this.config.timeoutMinutes} minutes`
    // })

    // Call activity callbacks
    this.activityCallbacks.forEach(callback => callback())
  }

  /**
   * Reset session
   */
  public resetSession(): void {
    const timeoutMs = this.config.timeoutMinutes * 60 * 1000
    const now = Date.now()
    this.state = {
      isActive: true,
      lastActivity: now,
      warningShown: false,
      timeRemaining: timeoutMs,
      initializedAt: now
    }
    console.log('[SessionManager] Session reset with timeout:', this.config.timeoutMinutes, 'minutes', 
                `(${timeoutMs}ms)`, 'Warning will show at:', this.config.warningMinutes, 'minutes remaining')
  }

  /**
   * Extend session
   */
  public extendSession(): void {
    this.recordActivity()
  }

  /**
   * Force logout
   */
  public forceLogout(): void {
    this.state.isActive = false
    this.stopMonitoring()
    if (this.timeoutCallback) {
      this.timeoutCallback()
    }
  }

  /**
   * Get current session state
   */
  public getState(): SessionState {
    return { ...this.state }
  }

  /**
   * Get time remaining in minutes
   */
  public getTimeRemainingMinutes(): number {
    return Math.ceil(this.state.timeRemaining / (60 * 1000))
  }

  /**
   * Check if session is active
   */
  public isSessionActive(): boolean {
    return this.state.isActive
  }

  /**
   * Start monitoring session
   */
  private startMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }

    this.intervalId = setInterval(() => {
      this.checkSession()
    }, this.config.checkIntervalMs)
  }

  /**
   * Stop monitoring session
   */
  private stopMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  /**
   * Check session status
   */
  private checkSession(): void {
    // Don't check if session timeout is disabled or session is not active
    if (!this.config.enabled || !this.state.isActive) return

    const now = Date.now()
    const timeSinceActivity = now - this.state.lastActivity
    const totalTimeoutMs = this.config.timeoutMinutes * 60 * 1000
    const warningTime = this.config.warningMinutes * 60 * 1000
    const timeRemaining = totalTimeoutMs - timeSinceActivity

    // Validate that timeRemaining makes sense
    // If timeRemaining is less than (timeout - 1 minute), something is wrong
    // This could happen if the config was updated after the session started
    if (timeRemaining < (totalTimeoutMs - 60000) && this.state.initializedAt) {
      const timeSinceInit = (now - this.state.initializedAt) / 1000
      const timeSinceActivitySeconds = timeSinceActivity / 1000
      
      // If we've been running for less than 1 minute but timeRemaining is already low,
      // the config might have been wrong at initialization
      if (timeSinceInit < 60 && timeRemaining < warningTime) {
        console.warn('[SessionManager] Detected incorrect timeout calculation. Resetting session.', {
          timeSinceInit: `${timeSinceInit.toFixed(1)}s`,
          timeSinceActivity: `${timeSinceActivitySeconds.toFixed(1)}s`,
          timeRemaining: `${Math.ceil(timeRemaining / (60 * 1000))}m`,
          expectedTimeout: `${this.config.timeoutMinutes}m`,
          actualTimeout: `${Math.ceil(totalTimeoutMs / (60 * 1000))}m`
        })
        // Reset the session with correct timeout
        this.resetSession()
        return
      }
    }
    
    // Debug log every 10 seconds to track session state
    if (Math.floor(now / 10000) !== Math.floor((now - 1000) / 10000)) {
      console.log('[SessionManager] Session check:', {
        timeoutMinutes: this.config.timeoutMinutes,
        timeSinceActivity: `${(timeSinceActivity / 1000).toFixed(1)}s`,
        timeRemaining: `${Math.ceil(timeRemaining / (60 * 1000))}m`,
        warningShown: this.state.warningShown
      })
    }

    this.state.timeRemaining = Math.max(0, timeRemaining)

    // Check if session has expired
    if (timeRemaining <= 0) {
      this.forceLogout()
      return
    }

    // Check if we should show warning (warningTime already calculated above)
    const minutesRemaining = Math.ceil(this.state.timeRemaining / (60 * 1000))
    
    // Don't show warning if session was just initialized or extended (within last 10 seconds)
    // This prevents false warnings when settings are loaded after initialization or after extending
    const justInitialized = this.state.initializedAt && (now - this.state.initializedAt) < 10000
    
    if (timeRemaining <= warningTime && !this.state.warningShown && !justInitialized) {
      console.log('[SessionManager] Showing warning:', {
        timeRemaining: minutesRemaining,
        warningThreshold: this.config.warningMinutes,
        timeoutMinutes: this.config.timeoutMinutes,
        timeSinceInit: this.state.initializedAt ? (now - this.state.initializedAt) / 1000 : 'N/A'
      })
      this.state.warningShown = true
      if (this.warningCallback) {
        this.warningCallback(timeRemaining)
      }
    } else if (justInitialized && timeRemaining <= warningTime) {
      console.log('[SessionManager] Warning suppressed - session just initialized:', {
        timeRemaining: minutesRemaining,
        timeSinceInit: (now - this.state.initializedAt!) / 1000
      })
    }
  }

  /**
   * Setup event listeners for user activity
   */
  private setupEventListeners(): void {
    if (typeof window === 'undefined') return

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ]

    const throttledActivity = this.throttle(() => {
      this.recordActivity()
    }, 1000) // Throttle to once per second

    events.forEach((event) => {
      document.addEventListener(event as keyof DocumentEventMap, throttledActivity as EventListener, true)
    })

    // Handle visibility change
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.recordActivity()
      }
    })
  }

  /**
   * Throttle function to limit calls
   */
  private throttle(func: () => void, limit: number): EventListener {
    let inThrottle: boolean
    return function (_event?: Event) {
      if (!inThrottle) {
        func()
        inThrottle = true
        setTimeout(() => (inThrottle = false), limit)
      }
    }
  }

  /**
   * Cleanup
   */
  public destroy(): void {
    this.stopMonitoring()
    this.state.isActive = false
  }
}

// Create singleton instance
export const sessionManager = new SessionManager()

// Export default config
export const defaultSessionConfig: SessionConfig = {
  timeoutMinutes: 30,
  warningMinutes: 5,
  checkIntervalMs: 1000,
  secure: true,
  enabled: true
}

